import { selectors, lib } from '@gocanvas/client'
const { checkBoxTrue, checkBoxFalse } = lib
const { conditionTypesSelector, entriesSelector } = selectors
import { WhiteLabelCheck, altSelectStyles, andOrSelectStyle } from '../shared'
import Select, { components } from 'react-select'
import { Input } from 'components/Input/Input'
import {
  entryConditions,
  sheetConditions,
  activeSheetIdSelector,
} from 'apps/builder/store/selectors'
import { eligibleDependentEntriesSelector } from 'apps/formsCreationKit/store/selectors/entities'
import { createDependentEntriesFilterFn } from 'apps/formsCreationKit/lib/utils'
import { useSelector } from 'react-redux'
import { useState, useRef, useEffect, Fragment } from 'react'
import { useDispatch } from 'react-redux'
import {
  updateCondition,
  deleteCondition,
  createCondition,
} from 'apps/builder/store/actions/conditions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faTrashAlt,
  faPlus,
  faAngleLeft,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons'
import {
  getConditionName,
  isTrueStringMatch,
  isEmpty,
  isBlank,
  conditionEntryTypeWhiteList
} from 'apps/builder/lib/utils'
import { ModernRadioButtonGroup } from 'apps/builder/components/shared/inputs/modern'
import { FoundryTooltip } from 'apps/builder/components/shared'

const ConditionsSelect = ({ autoFocus, ...props }) => {
  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef && autoFocus) {
      inputRef.current.focus()
    }
  }, [inputRef, autoFocus])

  return (
    <Select
      styles={altSelectStyles}
      isSearchable={false}
      className="conditions--dropdown field"
      classNamePrefix="react-select"
      blurInputOnSelect
      ref={inputRef}
      {...props}
    />
  )
}

export const Conditions = ({
  guid,
  sheet_id,
  conditionType,
  name,
  leftPanel = false,
}) => {
  const isScreen = conditionType === 'screen'
  const conditions = isScreen
    ? useSelector(sheetConditions)
    : useSelector(entryConditions)
  const finalConditions = useRef(conditions || [])
  const dispatch = useDispatch()
  const entryName = isScreen ? 'this screen' : 'this field'
  const conditionsMessage = `${name ? name : entryName} `

  // keep the list of conditions current
  // as we're not running the main useEffect except on open/teardown
  useEffect(() => {
    finalConditions.current = conditions
  }, [conditions])

  useEffect(() => {
    return () => {
      // clean up any incomplete conditions, close any in editing state
      if (finalConditions.current) {
        finalConditions.current.forEach(
          ({
            condition_type_id,
            value,
            entry,
            parentEntryGuid,
            id,
            editing,
          }) => {
            if (
              isEmpty(condition_type_id) || (!parentEntryGuid && !entry)
                            
            ) {
              dispatch(deleteCondition(id))
            } else if (editing) {
              dispatch(updateCondition({ id, editing: false }))
            }
          }
        )
      }
    }
  }, [])

  return (
    <div className="conditions--field">
      <ConditionHelpText
        showHelpText={!leftPanel}
        className="foundry--surface surface--info info--block"
      />
      <div className="conditions--wrapper">
        <div className="condition--message">
          Show{' '}
          <span className="condition--entry-name">{conditionsMessage}</span>
          when
        </div>
        {conditions.map((condition = {}, i) => {
          return (
            <Fragment key={`${condition.id}-${i}`}>
              <ConditionDropdownWrapper
                leftPanel={leftPanel}
                conditionType={conditionType}
                {...condition}
                showAndOr={i !== 0}
              />
            </Fragment>
          )
        })}
        <div className="conditions--button--container">
          <button
            className={`foundry--button button--outlined ${
              leftPanel
                ? 'button--condensed button--modern'
                : 'btn--round--border'
            }`}
            onClick={() => {
              dispatch(
                createCondition({
                  parentEntryGuid: guid,
                  sheet_id,
                  editing: true,
                })
              )
            }}
          >
            {!leftPanel && <FontAwesomeIcon icon={faPlus} />}
            <span
              className={`button--label ${!leftPanel ? 'btn--with--icon' : ''}`}
            >
              Add Condition
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}

const AndOrDropdown = ({ operator, updateConditionState }) => {
  const choices = [
    {
      label: 'and',
      value: 0,
    },
    {
      label: 'or',
      value: 1,
    },
  ]
  const value = choices.find(choice => choice.value === operator)
  return (
    <Select
      styles={andOrSelectStyle}
      isSearchable={false}
      value={value || choices[0]}
      className="conditions--dropdown and--or"
      classNamePrefix="react-select"
      options={choices}
      placeholder={choices[0].label}
      blurInputOnSelect
      onChange={val => {
        updateConditionState({
          data: {
            operator: val.value,
          },
        })
      }}
    />
  )
}

const AndOrRadioButton = ({ id, operator }) => {
  const dispatch = useDispatch()
  const choices = [
    {
      label: 'AND',
      value: 0,
    },
    {
      label: 'OR',
      value: 1,
    },
  ]

  return (
    <ModernRadioButtonGroup
      name={`conditionsOperator-${id}`}
      choices={choices}
      selectedValue={[0, 1].includes(operator) ? operator : 0}
      onChange={updatedValue => {
        dispatch(
          updateCondition({
            id,
            operator: parseInt(updatedValue, 10),
          })
        )
      }}
    />
  )
}

const checkboxOptions = [
  {
    label: 'Checked',
    value: 'True',
    matchValues: checkBoxTrue// these have the same effect as True
  },
  {
    label: 'Unchecked',
    value: 'False',
    matchValues: checkBoxFalse// these have the same effect as any value
  },
]


const ConditionSentence = ({ entry, condition_type_id, value, onClick }) => {
  const entries = useSelector(entriesSelector)

  const conditionTypes = useSelector(conditionTypesSelector)
  const entryField = entries.find(({ guid }) => guid === entry)
  const entryConditionType = conditionTypes.find(
    condition_type => condition_type.id === condition_type_id
  )

  const isCheckbox = entryField.type == 'Checkbox'
  const valueOutput = value === '' ? "empty" : value 

  const selectedCheckboxOption = isCheckbox && checkboxOptions.find(
    ({ matchValues }) => matchValues.includes(String(value).toLowerCase())
  )

  return (
    <div className="condition--button--wrapper">
      <button className="condition--button" onClick={onClick}>
        {`${entryField.label} `}
        <span className="condition--button__type">{`${getConditionName(
          entryConditionType
        )} `}</span>
        {isCheckbox ? `${selectedCheckboxOption ? selectedCheckboxOption.label : ''}` : `${valueOutput}`}
      </button>
    </div>
  )
}

const ConditionDropdownWrapper = ({
  id,
  showAndOr,
  value,
  editing,
  entrySheetId,
  condition_type_id,
  operator,
  entry,
  conditionType,
  leftPanel = false,
}) => {
  const dispatch = useDispatch()
  const valueInput = useRef(null)
  const entries = useSelector(entriesSelector)

  const updateConditionState = ({ data }) => {
    dispatch(updateCondition({ id, ...data }))
  }

  const determineActiveInput = () => {
    if (!entrySheetId || !entry) {
      return 'field'
    }

    if (!condition_type_id && condition_type_id !== 0) {
      return 'conditionType'
    }

    return 'valid'
  }

  const activeInput = determineActiveInput()

  if (activeInput === 'value') {
    valueInput?.current?.focus()
  }

  const disabled = activeInput !== 'valid'

  const screenFieldOptions = ({ innerProps }) => {
    return <div {...innerProps}></div>
  }

  const isChosenConditionEntryACheckboxField =
    entries.find(e => e.guid == entry)?.type == 'Checkbox'

  const selectedCheckboxOption = isChosenConditionEntryACheckboxField && checkboxOptions.find(
    ({ matchValues }) => matchValues.includes(String(value).toLowerCase())
  )

  // May still need to debounce input changes...
  return (
    <div className="condition--container">
      {(showAndOr && leftPanel) && (
        <AndOrRadioButton
          operator={operator}
          id={id}
        />
      )}
      {(showAndOr && !leftPanel) && (
        <AndOrDropdown
          operator={operator}
          updateConditionState={updateConditionState}
        />
      )}
      {!editing ? (
        <div onClick={() => updateConditionState({ data: { editing: true } })}>
          <ConditionSentence
            entry={entry}
            entrySheetId={entrySheetId}
            condition_type_id={condition_type_id}
            value={value}
          />
        </div>
      ) : (
        <div className="dropdown--wrapper">
          <ConditionScreenFieldDropdown
            conditionType={conditionType}
            updateConditionState={updateConditionState}
            entry={entry}
            entrySheetId={entrySheetId}
            autoFocus={activeInput === 'field'}
            placeholder="Field"
            screenFieldOptions={screenFieldOptions}
          />
          <ConditionTypeDropdown
            updateConditionState={updateConditionState}
            entry={entry}
            entrySheetId={entrySheetId}
            condition_type_id={condition_type_id}
            autoFocus={activeInput === 'conditionType'}
            placeholder="Condition"
          />
          {isChosenConditionEntryACheckboxField ? (
            <>
              <Select
                styles={altSelectStyles}
                isSearchable={false}
                value={selectedCheckboxOption}
                onChange={({ value }) =>
                  updateConditionState({ data: { value } })
                }
                placeholder="Value"
                className="conditions--dropdown value--checkbox"
                classNamePrefix="react-select"
                blurInputOnSelect
                options={checkboxOptions}
              />
            </>
          ) : (
            <div className={`preview--field condition--value--input--wrapper`}>
              <Input
                value={value}
                update={value => updateConditionState({ data: { value } })}
                placeholder="Value"
                valueRef={valueInput}
                additionalClass={'condition--value--input '}
                size={value ? value.length : '4'}
                label={''}
              />
            </div>
          )}

          <div className="condition--divider"></div>
          <div className="condition--btns">
            <span className={`${disabled ? 'disabled--condition--btn' : ''}`}>
              <button
                className={`condition--btn save ${disabled ? 'disabled' : ''}`}
                onClick={() =>
                  updateConditionState({ data: { editing: false } })
                }
                disabled={disabled}
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
            </span>
            <button
              className="condition--btn delete"
              onClick={() => dispatch(deleteCondition(id))}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const mapSheetToOption = ({
  description: label,
  placeholder,
  id: value,
  section_id,
  isSubSheet = false,
  primarySheet,
}) => {
  if (!isSubSheet) {
    // Default text if the user doesn't name their screen!
    if (isBlank(label)) {
      label = placeholder
    }
    return {
      label,
      value,
      section_id,
    }
  } else {
    label = primarySheet.description || placeholder
    return {
      label,
      value,
      section_id,
    }
  }
}

const mapEntryToOption = entry => {
  if (entry) {
    let label = entry.label
    // Default text if the user doesn't name their entry!
    if (label === '') {
      label = entry.placeholder
    }
    return {
      ...entry,
      label,
      value: entry.guid,
    }
  }
}

const entryConditionFilter = ({ original_type_id }) => conditionEntryTypeWhiteList.includes(original_type_id)

const ConditionScreenFieldDropdown = ({
  autoFocus,
  placeholder,
  updateConditionState,
  entrySheetId,
  entry,
  conditionType,
}) => {
  const activeSheetId = useSelector(activeSheetIdSelector)
  let eligibleConditionScreens = useSelector(eligibleDependentEntriesSelector)

  // filter out screens that are the same as the active sheet in screen conditions
  if (conditionType === 'screen') {
    eligibleConditionScreens = eligibleConditionScreens.filter(
      ({ id }) => !isTrueStringMatch(id, activeSheetId)
    )
  }

  // removed entry.isLinkedEntry check, not clear if that's needed w/ this
  const sheets = eligibleConditionScreens.reduce(
    createDependentEntriesFilterFn(entryConditionFilter),
    []
  )

  const entries = sheets.find(({ id }) => isTrueStringMatch(id, entrySheetId))?.entries || []
  const selectedEntry = entries.find(({ guid }) => guid === entry)

  const options = [
    {
      label: !entrySheetId ? 'What screen is the field in?' : 'Choose field',
      options: !entrySheetId
        ? sheets.map(mapSheetToOption)
        : entries.map(mapEntryToOption),
    },
  ]

  const [menuIsOpen, setMenuIsOpen] = useState(false)

  useEffect(() => {
    if (selectedEntry) {
      setMenuIsOpen(false)
    }
  }, [selectedEntry])

  const updateScreenFieldDropdown = value => {
    if (!entrySheetId) {
      updateConditionState({
        data: {
          entrySheetId: value.value,
          entry: '',
          condition_type_id: '',
          value: '',
        },
      })
    } else {
      const entryGuid = value.value
      updateConditionState({
        data: {
          entry: entryGuid,
        },
      })
    }
  }

  const onChange = value => {
    updateScreenFieldDropdown(value)
  }

  const onInputChange = () => {
    if (selectedEntry) {
      setMenuIsOpen(false)
    }
  }

  const onBlur = () => {
    if (selectedEntry) {
      setMenuIsOpen(false)
    }
  }

  const customGroupHeading = props => {
    if (entrySheetId) {
      return (
        <components.GroupHeading {...props}>
          <div className="custom-group-heading">
            <button
              onClick={() =>
                updateConditionState({
                  data: {
                    entrySheetId: null,
                    entry: '', // We may need more resetting here.
                    condition_type_id: '',
                    value: '',
                  },
                })
              }
              className="back-to-screens--button"
            >
              <div className="back-to-screens--text--wrapper">
                <FontAwesomeIcon icon={faAngleLeft} />
                <div className="back-to-screens--text">Back to screens</div>
              </div>
            </button>
            {props.children}
          </div>
        </components.GroupHeading>
      )
    }
    return (
      <components.GroupHeading {...props}>
        {props.children}
      </components.GroupHeading>
    )
  }

  const customControl = props => {
    props.innerProps.onMouseDown = () => setMenuIsOpen(!menuIsOpen)
    return <components.Control {...props}>{props.children}</components.Control>
  }

  const formatGroupLabel = data => (
    <div className="conditions-group--label-wrapper">
      <div className="conditions-group--step">
        STEP {`${entrySheetId ? 2 : 1}`} OF 2
      </div>
      <div className="conditions-group--label">{data.label}</div>
    </div>
  )

  const DropdownIndicator = props => {
    props.innerProps.onMouseDown = () => setMenuIsOpen(!menuIsOpen)
    return (
      <components.DropdownIndicator {...props}></components.DropdownIndicator>
    )
  }

  return (
    <ConditionsSelect
      value={selectedEntry}
      options={options}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={value => onChange(value)}
      onInputChange={onInputChange}
      onBlur={onBlur}
      menuIsOpen={menuIsOpen}
      controlShouldRenderValue={!!selectedEntry}
      formatGroupLabel={formatGroupLabel}
      components={{
        Control: customControl,
        GroupHeading: customGroupHeading,
        DropdownIndicator,
      }}
    />
  )
}

const mapConditionTypesToOption = condition_type => {
  const { description: label, id: value } = condition_type
  return {
    label,
    value,
  }
}

const ConditionTypeDropdown = ({
  autoFocus,
  entrySheetId,
  updateConditionState,
  condition_type_id,
  placeholder,
  entry,
}) => {
  const conditionTypes = useSelector(conditionTypesSelector)
  const options = conditionTypes.map(mapConditionTypesToOption)

  const isDisabled = !entrySheetId || entry?.length == 0

  const value =
    options.filter(option => option.value === condition_type_id) || ''

  return (
    <ConditionsSelect
      autoFocus={autoFocus}
      value={value}
      options={[{ label: 'Choose condition', options }]}
      placeholder={placeholder}
      isDisabled={isDisabled}
      onChange={value => {
        updateConditionState({
          data: {
            condition_type_id: value.value,
          },
        })
      }}
    />
  )
}

const ConditionHelpText = ({ showHelpText, className = '' }) => {
  return (
    showHelpText && (
      <div className={className}>
        <p>
          Say you only want to display a certain field or screen in your form
          if the response to a field is "No." Conditions allow users to skip
          screens and fields in the normal work flow if certain user defined
          conditions are met.{' '}
          <span>
            {' '}
            <WhiteLabelCheck>
              <a
                className="info--block--link"
                href="https://help.gocanvas.com/hc/en-us/articles/26359714183063-Adding-Conditions-in-the-Builder"
                target="blank"
              >
                Learn More
              </a>
            </WhiteLabelCheck>
          </span>
        </p>
      </div>
    )
  )
}

export const ConditionsHeader = () => (
  <>
    <span>Conditions</span>
    <div className="foundry--tooltip--wrapper">
      <FontAwesomeIcon icon={faInfoCircle} role="img" size="sm" />
      <FoundryTooltip
        label={<ConditionHelpText showHelpText={true} />}
        direction="right"
        size="medium"
      />
    </div>
  </>
)
